import {
  CFormCheck,
  CModal,
  CModalBody,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useMutation, useQueryClient } from "react-query";
import DeleteIcon from "../../components/icons/DeleteIcon";
import EditIcon from "../../components/icons/EditIcon";
import TranslateIcon from "../../components/icons/TranslateIcon";
import { FormSelectAPI } from "../../components/ui/FormReactSelect";
import PromptModal from "../../components/modals/PromptModal";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { useAuth } from "../../hooks/useAuth";
import { useCookie } from "../../hooks/useCookie";
import { useServiceSubCategoriesQuery } from "../../hooks/ServiceCategories/useSubServiceCategories";
import { usePaginatedContent } from "../../hooks/usePaginatedContent";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import NewSubCategory from "./NewSubCategory";
import { controlStyle, tdStyle, thStyle } from "../../helpers/constants";
import { Chip, IconAction } from "../../helpers/general";
import { cardStyle } from "../Places/PlacesTable";

const PAGE_SIZE = 10;

/* ─────────────── Edit modal ─────────────── */

function EditSubCategoryModal({
  subCategory,
  onClose,
}: {
  subCategory: ServiceSubCategory;
  onClose: () => void;
}) {
  const { token } = useCookie("vToken");
  const { toastError, toastSuccess } = useToast();
  const queryClient = useQueryClient();
  const [label, setLabel] = useState(subCategory.label ?? "");
  const [category, setCategory] = useState<ServiceCategory>(
    subCategory.category,
  );

  const update = useMutation((data: { label: string; categoryId: string }) =>
    apiService.MakePutRequest(
      `service-categories/sub-categories/${subCategory.id}`,
      data,
      token,
    ),
  );

  const save = () =>
    update.mutate(
      { label: label.trim(), categoryId: category?.id },
      {
        onError: () => toastError(general.fr.message.operationFailed),
        onSuccess() {
          queryClient.invalidateQueries("getAllServiceSubCategories");
          toastSuccess(general.fr.message.categoryUpdated);
          onClose();
        },
      },
    );

  const unchanged =
    (label.trim() === subCategory.label &&
      category?.id === subCategory.category?.id) ||
    label.trim() === "";

  return (
    <CModal visible onClose={onClose} alignment="center">
      <CModalBody>
        <h5 className="fs-5 mb-4 border-bottom pb-1">
          Mettre à jour la sous-catégorie
        </h5>
        <div className="mb-3">
          <label htmlFor="sub-category-label">Nom de la sous-catégorie</label>
          <input
            id="sub-category-label"
            type="text"
            className="custom-input w-100 mt-1"
            placeholder="Nom de la sous-catégorie"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="Category" className="d-block mb-1">
            Catégorie
          </label>
          <FormSelectAPI
            name="Category"
            id="Category"
            isClearable={false}
            getOptionLabel={(option: ServiceCategory) => option?.label}
            getOptionValue={(option: ServiceCategory) => option.id}
            isOptionSelected={(option: ServiceCategory) =>
              option?.id === category?.id
            }
            url={{ path: "service-categories" }}
            onChange={(c: ServiceCategory) => setCategory(c)}
            defaultValue={category}
            required
          />
        </div>

        <button
          type="button"
          onClick={save}
          disabled={update.isLoading || unchanged}
          className="d-flex align-items-center justify-content-center btn btn-danger shadow-primary w-100 mt-2 p-2 fw-bold text-white"
        >
          {update.isLoading ? (
            <>
              <CSpinner size="sm" /> {general.fr.wait}
            </>
          ) : (
            "Enregistrer"
          )}
        </button>
      </CModalBody>
    </CModal>
  );
}

/* ─────────────── Row ─────────────── */

interface RowProps {
  subCategory: ServiceSubCategory;
  deleteList: React.Dispatch<BulkDeleteActions>;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function SubCategoryRow({
  subCategory,
  deleteList,
  isAdmin,
  onEdit,
  onDelete,
}: RowProps) {
  const [isChecked, setIsChecked] = useState(false);

  const toggle = useCallback(
    (checked: boolean) => {
      setIsChecked(checked);
      deleteList({
        type: checked ? DELETE_ACTIONS.ADD : DELETE_ACTIONS.REMOVE,
        id: subCategory.id,
      });
    },
    [deleteList, subCategory.id],
  );

  // A row that unmounts (page change, search) must not stay selected
  useEffect(
    () => () => deleteList({ type: DELETE_ACTIONS.REMOVE, id: subCategory.id }),
    [deleteList, subCategory.id],
  );

  return (
    <CTableRow
      style={{
        backgroundColor: isChecked ? "rgba(240,129,63,0.06)" : undefined,
        boxShadow: isChecked ? `inset 3px 0 0 ${colors.primary}` : undefined,
      }}
    >
      <CTableDataCell style={tdStyle}>
        <div className="d-flex align-items-center gap-3">
          <CFormCheck
            checked={isChecked}
            onChange={(e) => toggle(e.target.checked)}
          />
          <span className="fw-semibold">{subCategory.label}</span>
        </div>
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        {subCategory.category?.label ?? "-"}
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        <div className="d-flex align-items-center justify-content-end gap-2">
          <IconAction title="Modifier" onClick={onEdit}>
            <EditIcon width="16" height="16" fill={colors.secondary} />
          </IconAction>
          {isAdmin && (
            <IconAction
              title="Traduire"
              to="/service-sub-category-translations/new"
              state={{ category: subCategory }}
            >
              <TranslateIcon width="18" height="18" fill="#000" />
            </IconAction>
          )}
          <IconAction title="Supprimer" danger onClick={onDelete}>
            <DeleteIcon width="18" height="18" fill={colors.primary} />
          </IconAction>
        </div>
      </CTableDataCell>
    </CTableRow>
  );
}

/* ─────────────── Tab ─────────────── */

export default function SubCategoriesTab() {
  const { isAdmin } = useAuth();
  const { categories, isLoading, deleteServiceCategory, mutating } =
    useServiceSubCategoriesQuery();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [addVisible, setAddVisible] = useState(false);
  const [toEdit, setToEdit] = useState<ServiceSubCategory | null>(null);
  const [toDelete, setToDelete] = useState<ServiceSubCategory | null>(null);

  const [selected, dispatch] = useReducer(bulkDeleteReducer, bulkDeleteState);
  const selectedCount = selected?.ids?.length ?? 0;

  // Search goes back to page 1
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filtered = useMemo(
    () =>
      (categories ?? []).filter((c: any) =>
        c.label?.toLowerCase()?.includes(searchQuery),
      ),
    [categories, searchQuery],
  );

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = usePaginatedContent(currentPage, PAGE_SIZE, filtered);

  const handleBulkDelete = () => {
    [...selected.ids].forEach((id: string) => {
      if (!id) return;
      deleteServiceCategory(id);
      dispatch({ type: DELETE_ACTIONS.REMOVE, id });
    });
  };

  const total = categories?.length ?? 0;

  return (
    <div>
      {/* Count + add */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        {categories && (
          <Chip tone="warning">
            {total} {total > 1 ? "Sous-catégories" : "Sous-catégorie"}
          </Chip>
        )}
        <button
          type="button"
          onClick={() => setAddVisible(true)}
          className="btn btn-primary text-white rounded-pill px-4 border-0"
        >
          + Ajouter une sous-catégorie
        </button>
      </div>

      {/* Toolbar */}
      <div
        style={{ ...cardStyle, padding: "12px 16px" }}
        className="d-flex flex-wrap align-items-center gap-2 mb-3"
      >
        <div
          className="d-flex align-items-center gap-2 px-3 flex-grow-1"
          style={{ ...controlStyle, minWidth: 200, maxWidth: 360 }}
        >
          <MagnifyingGlassIcon color="gray" width={18} />
          <input
            type="text"
            placeholder="Rechercher une sous-catégorie"
            className="border-0 w-100 bg-transparent"
            style={{ outline: "none", fontSize: 13 }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
          />
        </div>

        <button
          type="button"
          disabled={mutating || selectedCount === 0}
          onClick={handleBulkDelete}
          className="btn btn-outline-danger rounded-pill ms-auto d-flex align-items-center gap-2 px-3"
        >
          {mutating ? (
            <>
              <CSpinner size="sm" /> {general.fr.wait}
            </>
          ) : (
            <>
              <DeleteIcon fill={colors.primary} /> Supprimer
              {selectedCount > 0 && ` (${selectedCount})`}
            </>
          )}
        </button>
      </div>

      {/* Table */}
      <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
        <RenderTable
          loading={isLoading}
          render={() =>
            paginated?.length > 0 ? (
              <CTable responsive hover align="middle" className="mb-0">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell style={thStyle}>
                      Nom de la sous-catégorie
                    </CTableHeaderCell>
                    <CTableHeaderCell style={thStyle}>
                      Nom de la catégorie
                    </CTableHeaderCell>
                    <CTableHeaderCell
                      style={{ ...thStyle, textAlign: "right" }}
                    >
                      Actions
                    </CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {(paginated as ServiceSubCategory[]).map((subCategory) => (
                    <SubCategoryRow
                      key={subCategory.id}
                      subCategory={subCategory}
                      deleteList={dispatch}
                      isAdmin={Boolean(isAdmin)}
                      onEdit={() => setToEdit(subCategory)}
                      onDelete={() => setToDelete(subCategory)}
                    />
                  ))}
                </CTableBody>
              </CTable>
            ) : (
              <p className="text-center text-muted py-5 mb-0">
                {general.fr.noData}
              </p>
            )
          }
        />
      </div>

      <Pagination
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        siblingCount={1}
        totalCount={totalPages}
        pageSize={PAGE_SIZE}
      />

      {/* One modal of each kind for the whole table */}
      <CModal
        alignment="center"
        visible={addVisible}
        onClose={() => setAddVisible(false)}
        backdrop="static"
      >
        <NewSubCategory
          onAccept={() => {}}
          onClose={() => setAddVisible(false)}
        />
      </CModal>

      {toEdit && (
        <EditSubCategoryModal
          key={toEdit.id}
          subCategory={toEdit}
          onClose={() => setToEdit(null)}
        />
      )}

      <PromptModal
        open={toDelete !== null}
        onAccept={() => {
          if (toDelete) deleteServiceCategory(toDelete.id);
          setToDelete(null);
        }}
        onClose={() => setToDelete(null)}
      />
    </div>
  );
}
